import * as Types from '@aeppic/types'

import { getDocumentId } from '../../model/index.js'
import { Scopes } from '../../model/query/index.js'
import type Aeppic from 'aeppic/aeppic.js'
import { DesignLookup, DesignMatchStrategy, LookupTrace } from '../lookup/index.js'
import { OnceCache } from '@aeppic/shared/once'
import { useServerLookup } from '../lookup/client-lookup.js'

export function findFirstDocumentMatchingName(namedDocument: Types.Document[], names: string[]) {
  const designsByName = new Map(namedDocument.map(d => [d.data.name, d]))

  for (const name of names) {
    const bestMatch = designsByName.get(name)

    if (bestMatch) { 
      return bestMatch
    }
  }

  return null
}

const OPTIONAL_DESIGN_SELECTORS = new Set([
  'title-extension',
  'state',
  'page__additional-content',
])

function isOptionalDesignLookup(names: string): boolean {
  return names.split(',')
    .map(name => name.trim())
    .filter(Boolean)
    .every(name => OPTIONAL_DESIGN_SELECTORS.has(name))
}

export class Designs {
  private _designLookupCache: OnceCache<Types.Document, { formId: string, names: string }>
  private _fallbackDesignLookupCache: OnceCache<Types.Document, { names: string }>
  private get strategy(): DesignMatchStrategy | null {
    if (this._aeppic.Features?.isEnabled?.('design-lookup') === false) return null
    const name = this._aeppic.Features?.getOptionsFor?.('design-lookup')?.strategy ?? 'classic'
    const strategy = DesignMatchStrategy[String(name).toUpperCase().replace(/-/g, '_')]
    return typeof strategy === 'number' ? strategy : null
  }

  constructor(private _aeppic: Aeppic) {
    this._designLookupCache = new OnceCache('design-lookup', async ({ formId, names }) => {
      const strategy = this.strategy
      if (strategy === null) return this._getDesignWithClassicLookup(formId, names)
      if (!useServerLookup(this._aeppic, 'designs')) {
        return this._getDesignWithFullLookup(formId, names, strategy)
      }
      const result = await this._aeppic.lookupLocally?.({
        kind: 'design', formId, names: names.split(','), strategy: DesignMatchStrategy[strategy],
      }) ?? await this._fetchDesign(formId, names, strategy)
      const reference = result.reference ?? result.designId
      return reference ? this._aeppic.get(reference) : null
    })

    this._fallbackDesignLookupCache = new OnceCache('fallback-design-document', async ({ names }) => {
      const trimmedNames = names.split(',').filter(n => !!n).map(n => n.trim())

      return this._findBestMatchAtLocation('fallback-designs', trimmedNames)
    })
  }

  async _fetchDesign(formId: string, names: string, strategy: DesignMatchStrategy) {
    const params = new URLSearchParams({ names, strategy: DesignMatchStrategy[strategy] })
    const response = await this._aeppic.fetch(`/api/forms/${encodeURIComponent(formId)}/design?${params}`)
    if (!response.ok) throw new Error(`Design lookup failed: ${response.status}`)
    return response.json()
  }

  async _getDesignWithFullLookup(formId: string, names: string, strategy = DesignMatchStrategy.FULL) {
    const lookup = new DesignLookup(this._aeppic, strategy)
    for (const name of names.split(',').map(name => name.trim())) {
      const document = await lookup.findDesignDocument(formId, name)
      if (document) return document
    }
    return null
  }

  async _getDesignWithClassicLookup(formId: string, names: string) {
    const trimmedNames = names.split(',').filter(n => !!n).map(n => n.trim())
      
    const bestMatchUnderForm = await this._findBestMatchAtLocation(formId, trimmedNames)

    if (bestMatchUnderForm) {
      return bestMatchUnderForm
    }

    const bestMatchUnderFallbacks = await this._findBestMatchAtLocation('fallback-designs', trimmedNames)
    return bestMatchUnderFallbacks
  }

  clearCache() {
    this._designLookupCache.clear()
    this._fallbackDesignLookupCache.clear()
  }

  forDocument(document: Types.DocumentId | Types.IdOrReference | Types.Document) {
    return new DesignSelection(this, { document })
  }

  forForm(form: Types.DocumentId | Types.IdOrReference | Types.Document) {
    return new DesignSelection(this, { form })
  }

  document(document: Types.DocumentId | Types.IdOrReference | Types.Document) {
    return this.forDocument(document)
  }

  form(form: Types.DocumentId | Types.IdOrReference | Types.Document) {
    return this.forForm(form)
  }

  async inspectForDocument(document: Types.DocumentId | Types.IdOrReference | Types.Document, names: DesignSelectorInput, options: { strategy?: DesignMatchStrategy } = {}): Promise<DesignLookupResult> {
    const resolvedDocument = await this.resolveDocument(document)
    return this.inspectForForm(resolvedDocument?.f?.id, names, { document: resolvedDocument, strategy: options.strategy })
  }

  async hasConfiguredDesign(document: Types.Document, names: DesignSelectorInput): Promise<boolean> {
    const result = await this.inspectForDocument(document, names, { strategy: DesignMatchStrategy.CONFIGURED })
    return result.exists
  }

  async inspectForForm(form: Types.DocumentId | Types.IdOrReference | Types.Document, names: DesignSelectorInput, options: { document?: Types.Document, strategy?: DesignMatchStrategy } = {}): Promise<DesignLookupResult> {
    const formId = getFormId(form)
    const selectors = normalizeDesignSelectors(names)
    const strategy = options.strategy ?? DesignMatchStrategy.FULL

    for (const selector of selectors) {
      const trace = new LookupTrace()
      trace.formId = formId

      const lookup = new DesignLookup(this._aeppic, strategy)
      const design = await lookup.findDesignDocument(formId, selector, trace)

      if (design) {
        return this._buildLookupResult({
          exists: true,
          design,
          formId,
          selector,
          requestedSelectors: selectors,
          trace,
          document: options.document,
        })
      }
    }

    return {
      exists: false,
      design: null,
      formId,
      selector: selectors[0] ?? '',
      requestedSelectors: selectors,
      location: null,
      source: null,
      fallback: false,
      trace: null,
      document: options.document ?? null,
    }
  }

  async findDesignForDocument(document: Types.Document, names: string): Promise<Types.Document> {
    const cachedDocumentId = await this._designLookupCache?.get(JSON.stringify([this._aeppic.Account?.id ?? null, document.f.id, names.split(',').map(name => name.trim()), this.strategy, useServerLookup(this._aeppic, 'designs'), this._aeppic.LocalModel?.enabled === true]), { formId: document.f.id, names })

    if (cachedDocumentId === null) {
      return null
    } else if (cachedDocumentId) {
      return await this._aeppic.get(cachedDocumentId)
    } else if (!isOptionalDesignLookup(names)) {
      this._aeppic.Warn.once('design-lookup:access', JSON.stringify([document.f.id, names.split(',').map(name => name.trim())]), `Failed to find design for ${document.f.id} with names ${names}`)
    }
  }

  async findFallbackDesign(names: string): Promise<Types.Document|null> {
    return this._fallbackDesignLookupCache.get(names, { names })
  }

  async _findBestMatchAtLocation(locationId: string, names: string[]) {
    if (names.length === 0) {
      return null
    }

    const q = Scopes.children(locationId)
              .form('design-form')
              .where('data.name').isOneOf(names)

    const designsFromSource = await this._aeppic.find(q, { sort: 'modified.at' })

    for (const name of names) {
      const bestMatch = designsFromSource.find(d => d.data.name === name)

      if (bestMatch) {
        return bestMatch
      }
    }

    return null
  }

  async resolveDocument(document: Types.DocumentId | Types.IdOrReference | Types.Document): Promise<Types.Document> {
    const resolvedDocument = isFullDocument(document) ? document : await this._aeppic.get(getDocumentId(document))

    if (!resolvedDocument) {
      throw new Error(`Cannot resolve document for design lookup (${JSON.stringify(document)})`)
    }

    return resolvedDocument
  }

  private async _buildLookupResult(options: {
    exists: boolean
    design: Types.Document
    formId: string
    selector: string
    requestedSelectors: string[]
    trace: LookupTrace
    document?: Types.Document
  }): Promise<DesignLookupResult> {
    const locationId = findMatchLocationId(options.trace)
    const location = locationId ? await this._aeppic.get(locationId) : null
    const ownerId = findSourceOwnerId(options.formId, locationId, location, options.trace)
    const owner = ownerId ? await this._aeppic.get(ownerId) : null
    const source = classifyDesignSource(options.formId, locationId, location, owner, options.trace)

    return {
      exists: options.exists,
      design: options.design,
      formId: options.formId,
      selector: options.selector,
      requestedSelectors: options.requestedSelectors,
      location,
      source,
      fallback: source?.fallback ?? false,
      trace: options.trace,
      document: options.document ?? null,
    }
  }
}

export class DesignSelection {
  constructor(private _designs: Designs, private _scope: { document?: Types.DocumentId | Types.IdOrReference | Types.Document, form?: Types.DocumentId | Types.IdOrReference | Types.Document }) {
  }

  name(selector: DesignSelectorInput, name?: string) {
    return new DesignLookupSelection(this._designs, this._scope, normalizeDesignArguments(selector, name))
  }

  names(selectors: DesignSelectorInput) {
    return this.name(selectors)
  }

  design(selector: DesignSelectorInput, name?: string) {
    return this.name(selector, name)
  }

  designs(selectors: DesignSelectorInput) {
    return this.names(selectors)
  }
}

export class DesignLookupSelection {
  constructor(private _designs: Designs, private _scope: { document?: Types.DocumentId | Types.IdOrReference | Types.Document, form?: Types.DocumentId | Types.IdOrReference | Types.Document }, private _selectors: string[]) {
  }

  name(selector: DesignSelectorInput, name?: string) {
    return new DesignLookupSelection(this._designs, this._scope, [...this._selectors, ...normalizeDesignArguments(selector, name)])
  }

  names(selectors: DesignSelectorInput) {
    return this.name(selectors)
  }

  design(selector: DesignSelectorInput, name?: string) {
    return this.name(selector, name)
  }

  designs(selectors: DesignSelectorInput) {
    return this.names(selectors)
  }

  async info(): Promise<DesignLookupResult> {
    if (this._scope.document) {
      return this._designs.inspectForDocument(this._scope.document, this._selectors)
    }

    return this._designs.inspectForForm(this._scope.form, this._selectors)
  }

  async exists() {
    const info = await this.info()
    return info.exists
  }

  async get(): Promise<Types.Document | null> {
    const info = await this.info()
    return info.design
  }

  async required() {
    const info = await this.info()

    if (!info.design) {
      throw new Error(`No design found for form ${info.formId} and selectors "${this._selectors.join(',')}"`)
    }

    return info.design
  }
}

export interface DesignLookupResult {
  exists: boolean
  design: Types.Document | null
  formId: string
  selector: string
  requestedSelectors: string[]
  location: Types.Document | null
  source: DesignLookupSource | null
  fallback: boolean
  trace: LookupTrace | null
  document: Types.Document | null
}

export interface DesignLookupSource {
  kind: 'form' | 'design-folder' | 'referenced-folder' | 'system-fallback' | 'unknown'
  ownerKind: 'form' | 'application' | 'root' | 'system' | 'folder' | 'unknown'
  fallback: boolean
  documentId: string
  parentId?: string
  formId?: string
  owner?: Types.Document | null
}

export type DesignSelector = string | {
  namespace?: string
  name: string
}

export type DesignSelectorInput = DesignSelector | DesignSelector[]

function normalizeDesignArguments(selector: DesignSelectorInput, name?: string) {
  if (name == null) {
    return normalizeDesignSelectors(selector)
  }

  return normalizeDesignSelectors(formatDesignSelector({ namespace: String(selector), name }))
}

function normalizeDesignSelectors(selectors: DesignSelectorInput) {
  const selectorList = Array.isArray(selectors) ? selectors : [selectors]

  return selectorList
    .flatMap(selector => {
      if (typeof selector === 'string') {
        return selector.split(',')
      }

      return [formatDesignSelector(selector)]
    })
    .map(selector => selector.trim())
    .filter(Boolean)
}

function formatDesignSelector(selector: DesignSelector) {
  if (typeof selector === 'string') {
    return selector
  }

  return selector.namespace ? `${selector.namespace}:${selector.name}` : selector.name
}

function getFormId(form: Types.DocumentId | Types.IdOrReference | Types.Document) {
  if (!form) {
    throw new Error('Missing form for design lookup')
  }

  if (typeof form === 'string') {
    return form
  }

  if ((form as Types.Document).f?.id) {
    return (form as Types.Document).id
  }

  return (form as any).id
}

function isFullDocument(document: Types.DocumentId | Types.IdOrReference | Types.Document): document is Types.Document {
  return !!document && typeof document === 'object' && !!(document as Types.Document).f?.id
}

function findMatchLocationId(trace: LookupTrace) {
  const match = trace.steps.find(step => step.operation === 'find:match')
  return match?.documentId ?? null
}

function findSourceOwnerId(formId: string, locationId: string, location: Types.Document | null, trace: LookupTrace) {
  if (!locationId) {
    return null
  }

  if (locationId === formId || locationId === 'fallback-designs') {
    return locationId
  }

  const referencedFrom = findReferencedFolderOwner(locationId, trace)
  if (referencedFrom) {
    return referencedFrom.documentId
  }

  return location?.p ?? null
}

function classifyDesignSource(formId: string, locationId: string, location: Types.Document | null, owner: Types.Document | null, trace: LookupTrace): DesignLookupSource | null {
  if (!locationId) {
    return null
  }

  if (locationId === formId) {
    return {
      kind: 'form',
      ownerKind: 'form',
      fallback: false,
      documentId: locationId,
      formId,
      owner,
    }
  }

  if (locationId === 'fallback-designs') {
    return {
      kind: 'system-fallback',
      ownerKind: 'system',
      fallback: true,
      documentId: locationId,
      owner,
    }
  }

  const referencedFrom = findReferencedFolderOwner(locationId, trace)

  if (referencedFrom) {
    return {
      kind: 'referenced-folder',
      ownerKind: classifyOwner(owner),
      fallback: false,
      documentId: locationId,
      parentId: referencedFrom.documentId,
      owner,
    }
  }

  return {
    kind: location?.f?.id === 'design-folder' ? 'design-folder' : 'unknown',
    ownerKind: classifyOwner(owner),
    fallback: false,
    documentId: locationId,
    parentId: location?.p,
    owner,
  }
}

function findReferencedFolderOwner(locationId: string, trace: LookupTrace) {
  return trace.steps.find(step => {
    if (step.operation !== 'checkInReferencedFolders:form') {
      return false
    }

    const references = step.additionalInfo?.references ?? []
    return references.some(reference => reference?.id === locationId)
  })
}

function classifyOwner(owner: Types.Document | null): DesignLookupSource['ownerKind'] {
  if (!owner) {
    return 'unknown'
  }

  if (owner.id === 'root') {
    return 'root'
  }

  if (owner.id === 'system') {
    return 'system'
  }

  if (owner.f?.id === 'application-form') {
    return 'application'
  }

  if (owner.f?.id === 'form-folder') {
    return 'form'
  }

  if (owner.f?.id === 'root-folder-form') {
    return 'root'
  }

  return 'folder'
}
